import { every, isEmpty, map, pickBy } from "lodash";
import React from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import { useSelector } from "react-redux";
import { tColumn } from "src/hooks/table";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { setTableConfig } from "src/redux/TableConfig/actions";
import { tTables } from "src/redux/TableConfig/types";
import { checkColumns, generateCheckColumns } from "src/util/table";
import { PdSettings } from "../Icons";
import { Checkbox } from "../impComponents";
import { PdText } from "../layout";
import { PdDivider } from "../layout/table";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "../overlay";

type tConfigureTablePopover<T> = {
  columns: tColumn<T>[];
  tableConfigReduxKey?: tTables;
  onPressCheckbox?: (newTableConfig: Record<string, boolean>) => void;
};

const ConfigureTablePopover = <T extends unknown>({
  columns,
  tableConfigReduxKey,
  onPressCheckbox,
}: tConfigureTablePopover<T>): React.ReactElement => {
  const dispatch = useAppDispatch();
  const rTableConfigs = useSelector((state) =>
    tableConfigReduxKey ? state.TableConfigReducer[tableConfigReduxKey] : generateCheckColumns(columns),
  );

  const tableConfig = React.useMemo(
    () => pickBy(rTableConfigs, (_, key) => columns.map((column) => column.id).includes(key)),
    [columns, rTableConfigs],
  );
  const isAllSelected = every(tableConfig, (config) => config);

  const handleOnPressSelectAll = () => {
    const newTableConfig = checkColumns(columns);
    if (tableConfigReduxKey) dispatch(setTableConfig(tableConfigReduxKey, newTableConfig));
    onPressCheckbox?.(newTableConfig);
  };

  React.useEffect(() => {
    if (isEmpty(tableConfig) && tableConfigReduxKey) {
      dispatch(setTableConfig(tableConfigReduxKey, checkColumns(columns)));
    }
  }, [columns, dispatch, tableConfigReduxKey, tableConfig]);

  return (
    <PdPopover>
      <PdPopoverTrigger>
        <TouchableOpacity style={styles.configureTableButton}>
          <PdText>Configure Table</PdText>
          <PdSettings style={styles.headerButtonIcon} />
        </TouchableOpacity>
      </PdPopoverTrigger>
      <PdPopoverContent style={styles.configureTablePopover}>
        <PdText style={styles.configureTableTitle}>Select columns to view</PdText>
        <TouchableOpacity style={styles.configureTableTitleAllCheckbox} onPress={handleOnPressSelectAll}>
          <Checkbox style={styles.configureTableCheckbox} state={isAllSelected ? "on" : "off"} />
          <PdText style={styles.configureTableLabel}>Select All</PdText>
        </TouchableOpacity>
        <PdDivider style={styles.configureTableDivider} />
        {map(tableConfig, (isChecked, columnId) => {
          const column = columns.find((_column) => _column.id === columnId);
          const toggle = () => {
            const newTableConfig = { ...tableConfig, [columnId]: !tableConfig[columnId] };
            if (tableConfigReduxKey) dispatch(setTableConfig(tableConfigReduxKey, newTableConfig));
            onPressCheckbox?.(newTableConfig);
          };
          return (
            <TouchableOpacity key={columnId} style={styles.configureTableTitleHeaderCheckbox} onPress={toggle}>
              <Checkbox style={styles.configureTableCheckbox} state={isChecked ? "on" : "off"} />
              <PdText style={styles.configureTableLabel}>{column?.header}</PdText>
            </TouchableOpacity>
          );
        })}
      </PdPopoverContent>
    </PdPopover>
  );
};

export default ConfigureTablePopover;

const styles = StyleSheet.create({
  configureTableButton: {
    alignItems: "center",
    flexDirection: "row",
  },
  configureTableCheckbox: {
    height: 16,
    marginRight: kStyleConst.kSpacing * 2,
    width: 16,
  },
  configureTableDivider: {
    marginVertical: kStyleConst.kSpacing * 2,
  },
  configureTableLabel: {
    fontSize: 10,
    textTransform: "uppercase",
  },
  configureTablePopover: {
    backgroundColor: kColors.lightBrand,
    left: -150,
    paddingBottom: kStyleConst.kSpacing * 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 3,
    right: 0,
  },
  configureTableTitle: {
    color: kColors.gray500,
    fontSize: 10,
    fontWeight: "500",
    marginBottom: kStyleConst.kSpacing * 2,
    paddingRight: kStyleConst.kSpacing * 10,
    textTransform: "uppercase",
  },
  configureTableTitleAllCheckbox: {
    alignItems: "center",
    flexDirection: "row",
  },
  configureTableTitleHeaderCheckbox: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: kStyleConst.kSpacing * 2,
  },
  headerButtonIcon: {
    color: kColors.brandDark,
    height: 12,
    marginLeft: kStyleConst.kSpacing * 2,
    width: 12,
  },
});
